// Color tokens and visual config used across the app.
// Never hardcode colors in components — import from here.

export const SIDEBAR_BG = "#0f0f14";
export const MAIN_BG = "#f5f5fa";
export const SIDEBAR_WIDTH_EXPANDED = 260;
export const SIDEBAR_WIDTH_COLLAPSED = 72;
export const HEADER_HEIGHT = 64;

// Pipeline status badge colors. Keys are normalized status keys (lowercase snake).
// Falls back to `default` for unknown statuses.
export const statusColors: Record<
  string,
  { bg: string; text: string; border: string; dot: string }
> = {
  new: {
    bg: "bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-200",
    dot: "bg-slate-400",
  },
  qualified: {
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    border: "border-indigo-200",
    dot: "bg-indigo-500",
  },
  contacted: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
    dot: "bg-blue-500",
  },
  responded: {
    bg: "bg-cyan-50",
    text: "text-cyan-700",
    border: "border-cyan-200",
    dot: "bg-cyan-500",
  },
  call_booked: {
    bg: "bg-violet-50",
    text: "text-violet-700",
    border: "border-violet-200",
    dot: "bg-violet-500",
  },
  signed: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
    dot: "bg-emerald-500",
  },
  rejected: {
    bg: "bg-rose-50",
    text: "text-rose-700",
    border: "border-rose-200",
    dot: "bg-rose-500",
  },
  default: {
    bg: "bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-200",
    dot: "bg-slate-400",
  },
};

// Score color thresholds (text color).
export function scoreColor(score: number | null | undefined): string {
  if (score == null) return "text-slate-400";
  if (score >= 80) return "text-emerald-600";
  if (score >= 60) return "text-indigo-600";
  if (score >= 40) return "text-amber-600";
  return "text-slate-500";
}

// Score bar background color.
export function scoreBg(score: number | null | undefined): string {
  if (score == null) return "bg-slate-300";
  if (score >= 80) return "bg-emerald-500";
  if (score >= 60) return "bg-indigo-500";
  if (score >= 40) return "bg-amber-500";
  return "bg-slate-400";
}

// Contact enrichment status colors.
export const enrichmentColors: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  enriched: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  pending: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
  failed: {
    bg: "bg-rose-50",
    text: "text-rose-700",
    border: "border-rose-200",
  },
  none: {
    bg: "bg-slate-100",
    text: "text-slate-600",
    border: "border-slate-200",
  },
  default: {
    bg: "bg-slate-100",
    text: "text-slate-600",
    border: "border-slate-200",
  },
};

// Job run status colors (engine control).
export const runStatusColors: Record<
  string,
  { dot: string; text: string; bg: string }
> = {
  running: {
    dot: "bg-indigo-500",
    text: "text-indigo-700",
    bg: "bg-indigo-50",
  },
  // Backend may report either "success" or "succeeded" depending on the
  // code path that produced the run — map both to the same emerald
  // palette so the badge is correct either way.
  success: {
    dot: "bg-emerald-500",
    text: "text-emerald-700",
    bg: "bg-emerald-50",
  },
  succeeded: {
    dot: "bg-emerald-500",
    text: "text-emerald-700",
    bg: "bg-emerald-50",
  },
  failed: { dot: "bg-rose-500", text: "text-rose-700", bg: "bg-rose-50" },
  // Part of the work done, part failed (one campaign of several, say).
  partial: { dot: "bg-orange-500", text: "text-orange-700", bg: "bg-orange-50" },
  skipped: { dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50" },
  cancelled: { dot: "bg-slate-400", text: "text-slate-600", bg: "bg-slate-100" },
  paused: { dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50" },
  default: { dot: "bg-slate-400", text: "text-slate-600", bg: "bg-slate-100" },
};

export function statusKey(s: string | null | undefined): string {
  return (s ?? "").toLowerCase().replace(/[\s-]/g, "_");
}

/**
 * Resolve the badge classes for a pipeline status.
 *
 * Pass the project's effective pipeline statuses (from
 * `getEffectivePipelineStatuses(project)`) to honor the user's color choice
 * — including custom stages and renamed/recolored ones. Without statuses,
 * falls back to the static map so callers without project context still
 * render reasonably.
 */
export function getStatusColor(
  s: string | null | undefined,
  statuses?: readonly { key: string; color?: string }[],
) {
  const key = statusKey(s);
  if (statuses) {
    const match = statuses.find((p) => p.key === s || p.key === key);
    if (match?.color) {
      const tag = TAG_COLORS[match.color];
      if (tag) {
        return {
          bg: tag.pillBg,
          text: tag.pillText,
          border: tag.pillBorder,
          dot: tag.dot,
        };
      }
    }
  }
  return statusColors[key] ?? statusColors.default;
}

export function getEnrichmentColor(s: string | null | undefined) {
  return enrichmentColors[statusKey(s)] ?? enrichmentColors.default;
}

export function getRunStatusColor(s: string | null | undefined) {
  return runStatusColors[statusKey(s)] ?? runStatusColors.default;
}

// ─── Tags ─────────────────────────────────────────────────────────────────

export interface TagColorClasses {
  pillBg: string;
  pillText: string;
  pillBorder: string;
  dot: string;
  swatch: string;
}

export const TAG_COLORS: Record<string, TagColorClasses> = {
  gray:    { pillBg: "bg-slate-100",   pillText: "text-slate-700",   pillBorder: "border-slate-200",   dot: "bg-slate-400",   swatch: "bg-slate-400" },
  // Muted, deliberately NOT a red: the pipeline statuses that use it
  // (`unreachable`) are parked, not lost. Without an entry here the
  // backend-declared color falls through to the default slate pill.
  stone:   { pillBg: "bg-stone-100",   pillText: "text-stone-700",   pillBorder: "border-stone-300",   dot: "bg-stone-500",   swatch: "bg-stone-500" },
  red:     { pillBg: "bg-red-50",      pillText: "text-red-700",     pillBorder: "border-red-200",     dot: "bg-red-500",     swatch: "bg-red-500" },
  orange:  { pillBg: "bg-orange-50",   pillText: "text-orange-700",  pillBorder: "border-orange-200",  dot: "bg-orange-500",  swatch: "bg-orange-500" },
  amber:   { pillBg: "bg-amber-50",    pillText: "text-amber-800",   pillBorder: "border-amber-200",   dot: "bg-amber-500",   swatch: "bg-amber-500" },
  yellow:  { pillBg: "bg-yellow-50",   pillText: "text-yellow-800",  pillBorder: "border-yellow-200",  dot: "bg-yellow-500",  swatch: "bg-yellow-500" },
  lime:    { pillBg: "bg-lime-50",     pillText: "text-lime-700",    pillBorder: "border-lime-200",    dot: "bg-lime-500",    swatch: "bg-lime-500" },
  green:   { pillBg: "bg-green-50",    pillText: "text-green-700",   pillBorder: "border-green-200",   dot: "bg-green-500",   swatch: "bg-green-500" },
  emerald: { pillBg: "bg-emerald-50",  pillText: "text-emerald-700", pillBorder: "border-emerald-200", dot: "bg-emerald-500", swatch: "bg-emerald-500" },
  teal:    { pillBg: "bg-teal-50",     pillText: "text-teal-700",    pillBorder: "border-teal-200",    dot: "bg-teal-500",    swatch: "bg-teal-500" },
  cyan:    { pillBg: "bg-cyan-50",     pillText: "text-cyan-700",    pillBorder: "border-cyan-200",    dot: "bg-cyan-500",    swatch: "bg-cyan-500" },
  blue:    { pillBg: "bg-blue-50",     pillText: "text-blue-700",    pillBorder: "border-blue-200",    dot: "bg-blue-500",    swatch: "bg-blue-500" },
  indigo:  { pillBg: "bg-indigo-50",   pillText: "text-indigo-700",  pillBorder: "border-indigo-200",  dot: "bg-indigo-500",  swatch: "bg-indigo-500" },
  violet:  { pillBg: "bg-violet-50",   pillText: "text-violet-700",  pillBorder: "border-violet-200",  dot: "bg-violet-500",  swatch: "bg-violet-500" },
  purple:  { pillBg: "bg-purple-50",   pillText: "text-purple-700",  pillBorder: "border-purple-200",  dot: "bg-purple-500",  swatch: "bg-purple-500" },
  fuchsia: { pillBg: "bg-fuchsia-50",  pillText: "text-fuchsia-700", pillBorder: "border-fuchsia-200", dot: "bg-fuchsia-500", swatch: "bg-fuchsia-500" },
  pink:    { pillBg: "bg-pink-50",     pillText: "text-pink-700",    pillBorder: "border-pink-200",    dot: "bg-pink-500",    swatch: "bg-pink-500" },
  rose:    { pillBg: "bg-rose-50",     pillText: "text-rose-700",    pillBorder: "border-rose-200",    dot: "bg-rose-500",    swatch: "bg-rose-500" },
};

export const TAG_COLOR_KEYS = Object.keys(TAG_COLORS);

export function getTagColor(color: string | null | undefined): TagColorClasses {
  return TAG_COLORS[color ?? ""] ?? TAG_COLORS.gray;
}

